/**
 * Button-triggered modal that reuses the theme's `.modal` styles.
 */
(() => {
  if (customElements.get("theme-modal")) {
    return;
  }

  class ThemeModal extends HTMLElement {
    static DURATION = 500;

    connectedCallback() {
      this.dialog = this.querySelector("dialog");
      if (!this.dialog || this._bound) return;
      this._bound = true;

      this.dialog.addEventListener("click", (event) => {
        if (event.target.closest("[data-theme-modal-close]")) this.hide();
      });

      this.dialog.addEventListener("cancel", (event) => {
        event.preventDefault();
        this.hide();
      });
    }

    get isOpen() {
      return this.dialog?.open && this.dialog.classList.contains("active");
    }

    show(opener = null) {
      if (!this.dialog || this.isOpen) return;
      clearTimeout(this._closeTimer);

      this.opener = opener || document.activeElement;
      this.dialog.showModal();
      ThemeModal.lockScroll();

      requestAnimationFrame(() => {
        this.dialog.classList.add("active");
        const target =
          this.dialog.querySelector("[autofocus]") ||
          this.dialog.querySelector(
            "[data-theme-modal-close].modal__close-icon",
          );
        target?.focus({ preventScroll: true });
      });
      this.opener?.setAttribute?.("aria-expanded", "true");
      this.dispatchEvent(
        new CustomEvent("theme-modal:open", { bubbles: true }),
      );
    }

    hide() {
      if (!this.dialog?.open) return;

      this.dialog.classList.remove("active");
      this.opener?.setAttribute?.("aria-expanded", "false");

      this._closeTimer = setTimeout(() => {
        this.dialog.close();
        ThemeModal.unlockScroll();
        this.opener?.focus?.({ preventScroll: true });
        this.opener = null;
        this.dispatchEvent(
          new CustomEvent("theme-modal:close", { bubbles: true }),
        );
      }, ThemeModal.DURATION);
    }

    static openCount = 0;

    static lockScroll() {
      if (ThemeModal.openCount++ > 0) return;
      const scrollbarGap =
        window.innerWidth - document.documentElement.clientWidth;
      document.documentElement.style.overflow = "hidden";
      if (scrollbarGap > 0)
        document.body.style.paddingRight = `${scrollbarGap}px`;
      // Theme hook: hides chat/rewards widgets while an overlay is open
      document.body.setAttribute("data-fluorescent-overlay-open", "true");
    }

    static unlockScroll() {
      ThemeModal.openCount = Math.max(0, ThemeModal.openCount - 1);
      if (ThemeModal.openCount > 0) return;
      document.documentElement.style.overflow = "";
      document.body.style.paddingRight = "";
      document.body.setAttribute("data-fluorescent-overlay-open", "false");
    }
  }

  document.addEventListener("click", (event) => {
    const trigger = event.target.closest("[data-theme-modal-open]");
    if (!trigger) return;
    const modal = document.getElementById(trigger.dataset.themeModalOpen);
    if (!(modal instanceof ThemeModal)) return;
    event.preventDefault();
    modal.show(trigger);
  });

  customElements.define("theme-modal", ThemeModal);
})();
